import React, { useEffect } from "react";
import { FormInput, InputType } from "../../FormComponents/Input/Input";
import SkynetIcon from "../../Fundamentals/SkynetIcon";

const ROOT_PATH = "$";

function lookupSchema(schema, path) {
    if (!schema) return null;
    if (schema[path]) return schema[path];
    const pattern = path.replace(/\[\d+\]/g, "[*]");
    return schema[pattern] ?? null;
}

function getValueAtPath(root, path) {
    if (path === ROOT_PATH) return root;
    let current = root;
    const tokens = tokenizePath(path);
    for (const token of tokens) {
        if (current == null) return undefined;
        current = current[token];
    }
    return current;
}

function setValueAtPath(root, path, newValue) {
    if (path === ROOT_PATH) return newValue;
    const tokens = tokenizePath(path);
    return cloneAndSet(root, tokens, newValue);
}

function cloneAndSet(node, tokens, newValue) {
    if (tokens.length === 0) return newValue;
    const [head, ...rest] = tokens;
    if (typeof head === "number") {
        const arr = Array.isArray(node) ? [...node] : [];
        arr[head] = cloneAndSet(arr[head], rest, newValue);
        return arr;
    }
    const obj = node && typeof node === "object" && !Array.isArray(node) ? { ...node } : {};
    obj[head] = cloneAndSet(obj[head], rest, newValue);
    return obj;
}

function tokenizePath(path) {
    const tokens = [];
    if (!path || path === ROOT_PATH) return tokens;
    const rest = path.startsWith(ROOT_PATH) ? path.slice(1) : path;
    const re = /\.([A-Za-z_][A-Za-z0-9_]*)|\[(\d+)\]/g;
    let match;
    while ((match = re.exec(rest)) !== null) {
        if (match[1] != null) tokens.push(match[1]);
        else tokens.push(Number(match[2]));
    }
    return tokens;
}

function defaultForShape(template) {
    if (template == null) return null;
    if (Array.isArray(template)) return [];
    if (typeof template === "object") {
        const out = {};
        for (const [k, v] of Object.entries(template)) {
            out[k] = cloneValue(v);
        }
        return out;
    }
    return cloneValue(template);
}

function cloneValue(v) {
    if (v == null) return v;
    if (Array.isArray(v)) return v.map(cloneValue);
    if (typeof v === "object") {
        const out = {};
        for (const [k, val] of Object.entries(v)) out[k] = cloneValue(val);
        return out;
    }
    return v;
}

function blankShape(v) {
    if (v == null) return null;
    if (Array.isArray(v)) return [];
    if (typeof v === "object") {
        const out = {};
        for (const [k, val] of Object.entries(v)) out[k] = blankShape(val);
        return out;
    }
    if (typeof v === "string") return "";
    if (typeof v === "number") return 0;
    if (typeof v === "boolean") return false;
    return null;
}

function inferTemplate(existingArray) {
    if (!Array.isArray(existingArray) || existingArray.length === 0) return null;
    return blankShape(existingArray[0]);
}

export function JsonTreeEditor({
    value,
    schema,
    errors,
    onChange,
    onEditingStateChange,
}) {
    useEffect(() => {
        if (onEditingStateChange) onEditingStateChange(false);
    }, [onEditingStateChange]);

    const setAt = (path, newValue) => {
        const next = setValueAtPath(value, path, newValue);
        onChange(next, path);
    };

    return (
        <Node
            value={value}
            path={ROOT_PATH}
            schema={schema}
            errors={errors}
            setAt={setAt}
            isRoot
        />
    );
}

function Node({ value, path, schema, errors, setAt, label, isRoot }) {
    if (Array.isArray(value)) {
        return (
            <ArrayNode
                value={value}
                path={path}
                schema={schema}
                errors={errors}
                setAt={setAt}
                label={label}
                isRoot={isRoot}
            />
        );
    }
    if (value !== null && typeof value === "object") {
        return (
            <ObjectNode
                value={value}
                path={path}
                schema={schema}
                errors={errors}
                setAt={setAt}
                label={label}
                isRoot={isRoot}
            />
        );
    }
    return (
        <PrimitiveNode
            value={value}
            path={path}
            schema={schema}
            errors={errors}
            setAt={setAt}
            label={label}
        />
    );
}

function ObjectNode({ value, path, schema, errors, setAt, label, isRoot }) {
    return (
        <fieldset className={isRoot ? "" : "ml-16 mb-12 pl-12 border-left"}>
            {label && <legend className="font-weight-bold">{label}</legend>}
            {Object.entries(value).map(([key, child]) => {
                const childPath = `${path}.${key}`;
                return (
                    <Node
                        key={childPath}
                        value={child}
                        path={childPath}
                        schema={schema}
                        errors={errors}
                        setAt={setAt}
                        label={key}
                    />
                );
            })}
        </fieldset>
    );
}

function ArrayNode({ value, path, schema, errors, setAt, label, isRoot }) {
    const schemaNode = lookupSchema(schema, path);
    const template = schemaNode?.template ?? inferTemplate(value);

    const onAdd = () => {
        const newItem = defaultForShape(template);
        setAt(path, [...value, newItem]);
    };

    const onDelete = (idx) => {
        const next = value.filter((_, i) => i !== idx);
        setAt(path, next);
    };

    return (
        <fieldset className={isRoot ? "" : "ml-16 mb-12 pl-12 border-left"}>
            {label && <legend className="font-weight-bold">{label}</legend>}
            <div className="d-flex flex-column gap-8">
                {value.map((item, idx) => {
                    const itemPath = `${path}[${idx}]`;
                    return (
                        <div key={itemPath} className="d-flex align-items-start gap-12 p-8 border rounded-8">
                            <div className="flex-grow-1">
                                <Node
                                    value={item}
                                    path={itemPath}
                                    schema={schema}
                                    errors={errors}
                                    setAt={setAt}
                                    label={`#${idx + 1}`}
                                />
                            </div>
                            <SkynetIcon
                                icon={"vismaicon-delete vismaicon-sm"}
                                title="Delete"
                                onClick={() => onDelete(idx)}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="mt-12">
                <button type="button" className="btn btn-primary btn-sm" onClick={onAdd}>
                    Add
                </button>
            </div>
        </fieldset>
    );
}

function PrimitiveNode({ value, path, schema, errors, setAt, label }) {
    const schemaNode = lookupSchema(schema, path);
    const error = errors?.[path];

    if (schemaNode?.enumValues?.length) {
        return (
            <div className="form-group">
                {label && <label>{label}</label>}
                <select
                    className="form-control"
                    value={value ?? ""}
                    onChange={(e) => setAt(path, e.target.value)}
                >
                    {schemaNode.enumValues.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
                {error && <div className="text-danger">{error}</div>}
            </div>
        );
    }

    if (typeof value === "boolean") {
        return (
            <div className="form-group">
                <label className="d-flex align-items-center gap-8">
                    <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setAt(path, e.target.checked)}
                    />
                    {label}
                </label>
                {error && <div className="text-danger">{error}</div>}
            </div>
        );
    }

    if (typeof value === "number") {
        return (
            <FormInput
                type={InputType.NUMBER}
                label={label}
                value={value}
                onChange={(v) => {
                    if (v === "" || v == null) {
                        setAt(path, null);
                        return;
                    }
                    const n = Number(v);
                    setAt(path, Number.isFinite(n) ? n : v);
                }}
                error={error}
            />
        );
    }

    return (
        <FormInput
            label={label}
            value={value ?? ""}
            onChange={(v) => setAt(path, value === null && v === "" ? null : v)}
            error={error}
        />
    );
}
